import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Image,
  Eye,
  AlertCircle,
  TrendingUp,
  Plus,
  MessageSquare,
  Mail,
  Megaphone,
  Rocket,
  Camera,
  Home,
  Building2,
  Mountain,
  BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pageStats, setPageStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [dashboardRes, overviewRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers }),
          axios.get(`${API}/admin/stats/overview`, { headers }).catch(() => ({ data: null }))
        ]);
        
        setStats(dashboardRes.data);
        setPageStats(overviewRes.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    {
      title: "Total Posts",
      value: stats?.total_posts || 0,
      icon: FileText,
      color: "bg-blue-500",
    },
    {
      title: "Publicados",
      value: stats?.published_posts || 0,
      icon: Eye,
      color: "bg-green-500",
    },
    {
      title: "Borradores",
      value: stats?.draft_posts || 0,
      icon: FileText,
      color: "bg-yellow-500",
    },
    {
      title: "Sin Imagen",
      value: stats?.posts_without_images || 0,
      icon: AlertCircle,
      color: "bg-red-500",
    },
    {
      title: "Media Files",
      value: stats?.total_media || 0,
      icon: Image,
      color: "bg-purple-500",
    },
    {
      title: "Mensajes",
      value: stats?.unread_messages || 0,
      icon: MessageSquare,
      color: "bg-pink-500",
    },
  ];

  const solicitudesCards = [
    {
      title: "Contacto",
      pending: stats?.solicitudes?.contacto?.pending || 0,
      total: stats?.solicitudes?.contacto?.total || 0,
      icon: Mail,
      color: "bg-blue-500",
      href: "/admin/contacto"
    },
    {
      title: "Emprendedores",
      pending: stats?.solicitudes?.emprendedores?.pending || 0,
      total: stats?.solicitudes?.emprendedores?.total || 0,
      icon: Rocket,
      color: "bg-orange-500",
      href: "/admin/emprendedores"
    },
    {
      title: "Publicidad",
      pending: stats?.solicitudes?.publicidad?.pending || 0,
      total: stats?.solicitudes?.publicidad?.total || 0,
      icon: Megaphone,
      color: "bg-green-500",
      href: "/admin/publicidad"
    },
    {
      title: "Contenido Comunitario",
      pending: stats?.solicitudes?.contenido_comunitario?.pending || 0,
      total: stats?.solicitudes?.contenido_comunitario?.total || 0,
      icon: Camera,
      color: "bg-purple-500",
      href: "/admin/contenido-comunitario"
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Dashboard
            </h1>
            <p className="text-brand-textSecondary mt-1">
              Bienvenido al panel de administración
            </p>
          </div>
          <Link to="/admin/posts/new">
            <Button className="rounded-full" data-testid="new-post-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Post
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-12 bg-gray-200 rounded mb-4" />
                  <div className="h-8 w-16 bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {statCards.map((stat) => (
              <Card key={stat.title} className="hover:shadow-card-hover transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-brand-textSecondary">
                        {stat.title}
                      </p>
                      <p className="text-3xl font-bold text-brand-text mt-2">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-2xl`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Solicitudes Section */}
        {!loading && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Solicitudes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {solicitudesCards.map((item) => (
                  <Link key={item.title} to={item.href}>
                    <div className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors cursor-pointer border border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`${item.color} p-2 rounded-lg`}>
                          <item.icon className="h-5 w-5 text-white" />
                        </div>
                        {item.pending > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {item.pending} pendiente{item.pending !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-brand-text">{item.title}</h3>
                      <p className="text-sm text-brand-textSecondary">
                        {item.total} total
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Post Types */}
        {stats?.post_types && Object.keys(stats.post_types).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Posts por Tipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(stats.post_types).map(([type, count]) => {
                  const labels = {
                    news: "Noticias",
                    destination: "Destinos",
                    experience: "Experiencias",
                    flavor: "Sabores",
                    video: "Videos",
                    page: "Páginas",
                  };
                  return (
                    <div
                      key={type}
                      className="bg-brand-bg rounded-xl p-4 text-center"
                    >
                      <p className="text-2xl font-bold text-primary">{count}</p>
                      <p className="text-sm text-brand-textSecondary">
                        {labels[type] || type}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Page Views Stats */}
        {pageStats && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Visitas del Sitio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                {/* Home Page Views */}
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Home className="h-6 w-6" />
                    </div>
                    <span className="font-medium">Página Principal</span>
                  </div>
                  <p className="text-4xl font-bold mb-1">
                    {pageStats.page_views?.home?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-white/80">
                    Hoy: {pageStats.page_views?.home?.today || 0} visitas
                  </p>
                </div>

                {/* Directorio Views */}
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Building2 className="h-6 w-6" />
                    </div>
                    <span className="font-medium">Directorio</span>
                  </div>
                  <p className="text-4xl font-bold mb-1">
                    {pageStats.page_views?.directorio?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-white/80">
                    Hoy: {pageStats.page_views?.directorio?.today || 0} visitas
                  </p>
                </div>

                {/* Destinos Views */}
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Mountain className="h-6 w-6" />
                    </div>
                    <span className="font-medium">Destinos</span>
                  </div>
                  <p className="text-4xl font-bold mb-1">
                    {pageStats.page_views?.destinos?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-white/80">
                    {pageStats.content_counts?.destinations || 0} destinos publicados
                  </p>
                </div>
              </div>

              {/* Top Businesses */}
              {pageStats.top_businesses && pageStats.top_businesses.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Negocios más visitados
                  </h4>
                  <div className="space-y-2">
                    {pageStats.top_businesses.map((biz, index) => (
                      <div key={biz.business_id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="font-medium text-gray-800">{biz.name}</span>
                        </div>
                        <span className="text-sm text-gray-500">{biz.view_count} visitas</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/admin/posts/new">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <FileText className="h-5 w-5" />
                  Nueva Noticia
                </Button>
              </Link>
              <Link to="/admin/media">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <Image className="h-5 w-5" />
                  Subir Imagen
                </Button>
              </Link>
              <Link to="/admin/import">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <Plus className="h-5 w-5" />
                  Importar XML
                </Button>
              </Link>
              <Link to="/admin/posts?filter=no-image">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Posts sin Imagen
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
